import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CanvasSeatMapCanvas from '../../components/SeatMap/CanvasSeatMapCanvas';
import { getseatingbyId } from '../../services/apiService';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPhone, faWhatsapp, faMapMarkerAlt, faCalendarAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp as faWhatsappBrand } from '@fortawesome/free-brands-svg-icons';
import './ReadOnlyBookingPage.css';
import { getEventBySlugFromStorage, isEventOver } from '../../utils/urlUtils';
import { formatEventDateTimeFromAPI } from '../../utils/timeUtils';

const transformSeatMapData = (raw) => {
  return raw.map((seat) => {
    if (seat.type === 'space') {
      return {
        id: seat.id,
        type: 'space',
        row: seat.row,
        section: seat.section,
        status: 'space',
      };
    }
    return {
      id: seat.id,
      row: seat.row,
      number: parseInt(seat.label, 10),
      status: seat.reserved === true ? 'booked' : 'available',
      section: seat.section,
      price: seat.price,
      type: 'seat',
      color: seat.color
    };
  });
};

const transformApiSeatData = (apiData) => {
  // Get the last seating plan from the API data array
  const lastSeatingPlan = Array.isArray(apiData) ? apiData[apiData.length - 1] : apiData;
  if (!lastSeatingPlan) return [];
  console.log(lastSeatingPlan, "lastSeatingPlan");
  const transformedSeats = [];
  lastSeatingPlan.sections.forEach(section => {
    section.rows.forEach(row => {
      row.seats.forEach(seat => {
        // Treat as space if price is null and (level is empty or seatNumber is empty/null)
        if (
          seat.price === null || seat.price === 0 && 
          (seat.level === "" || seat.seatNumber === "" || seat.seatNumber == null)
        ) {
          transformedSeats.push({
            id: seat._id,
            label: '',
            row: section.name,
            type: 'space',
            section: section.name,
            price: null
          });
        } else {
          transformedSeats.push({
            id: seat._id,
            label: seat.seatNumber ? seat.seatNumber.toString() : '',
            number: seat.seatNumber,
            row: section.name,
            type: seat.reserved ? 'booked' : 'seat',
            section: section.name,
            price: seat.price,
            reserved: seat.reserved,
            color: seat.color
          });
        }
      });
    });
  });

  return transformedSeats;
};

// Utility function to safely format dates
const formatEventDateTime = (datetime) => {
  try {
    const eventDate = new Date(datetime);
    if (isNaN(eventDate.getTime())) {
      return 'Date not available';
    }
    
    // Format date
    const dateStr = eventDate.toLocaleDateString();
    
    // Format time in 12-hour format with AM/PM
    const timeStr = eventDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    return `${dateStr} • ${timeStr}`;
  } catch (error) {
    return 'Date not available';
  }
};

const ReadOnlyBookingPage = () => {
  const { eventName } = useParams();
  const navigate = useNavigate();
  const [seats, setSeats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [eventData, setEventData] = useState(location.state);

  // Get event data from sessionStorage if not available from location.state
  useEffect(() => {
    if (!eventData && eventName) {
      // Try to find event by slug from storage
      const foundEvent = getEventBySlugFromStorage(eventName);
      if (foundEvent) {
        setEventData(foundEvent);
        // Store as selectedEvent for other components
        sessionStorage.setItem("selectedEvent", JSON.stringify(foundEvent));
      }
    }
  }, [eventData, eventName]);

  const fetchSeatingPlan = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const eventDataToUse = eventData || JSON.parse(sessionStorage.getItem("selectedEvent"));
      if (!eventDataToUse?.seatingPlan) {
        setError('Event data not found. Please return to the event page.');
        return;
      }
      
      const seatingPlan = eventDataToUse.seatingPlan;
      console.log('Fetching seating plan for:', seatingPlan);
      
      // Direct API call
      const response = await getseatingbyId(seatingPlan);
     
      // Get the last seating plan from the array
      const apiData = response;
      const transformedApiData = transformApiSeatData(apiData);
      const transformed = transformSeatMapData(transformedApiData);
      setSeats(transformed);
    } catch (err) {
      console.error('Error fetching seating plan:', err);
      setError('Failed to load seating plan. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (eventData) {
      fetchSeatingPlan();
    }
  }, [eventName, eventData]);

  // Dummy function for read-only mode - no seat selection
  const dummyToggleSeat = () => {
    // Do nothing - seats cannot be selected in read-only mode
  };

  if (!eventData) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <h4 className="text-muted mb-3">Event data not found</h4>
            <p className="text-muted mb-4">Please return to the event page to continue.</p>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate(`/events/${eventName}`)}
            >
              Back to Event
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if event is over
  if (isEventOver(eventData.datetime)) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <h4 className="text-warning mb-3">Event Has Ended</h4>
            <p className="text-muted mb-4">This event has already taken place. Seating information is no longer available.</p>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate(`/events/${eventName}`)}
            >
              View Event Details
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading seating plan...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <h4 className="text-danger mb-3">Error Loading Seating Plan</h4>
            <p className="text-muted mb-4">{error}</p>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate(`/events/mauk/${eventName}`)}
            >
              Back to Event
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { title, datetime, venue, image } = eventData;
  
  // Helper to get formatted datetime for links
  const getFormattedDateTimeForLinks = () => {
    return formatEventDateTimeFromAPI(eventData.date, eventData.time);
  };

  // Contact information - you can customize these
  const contactInfo = {
    email: "info@mauk.org",
    whatsapp: "+447846066476",
    address: venue?.address || "Event Venue Address"
  };

  return (
    <>
      <section className="container-fluid container-xxl px-md-5 py-5">
        <a className="btn btn-link mb-4" onClick={() => navigate(`/events/mauk/${eventName}`)} style={{ width: 'fit-content' }}>
          <svg className='pe-1' width='1.125rem' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M512 256A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM215 127c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-71 71L392 232c13.3 0 24 10.7 24 24s-10.7 24-24 24l-214.1 0 71 71c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L103 273c-9.4-9.4-9.4-24.6 0-33.9L215 127z"/></svg> Back to Event
        </a>

        <div className="row pb-5">
          <div className="col-md-7 border-end mb-md-0 mb-5">
            
            <div className="alert alert-info" role="alert">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faClock} className="me-2" />
                <div>
                  This seating plan shows current seat availability. 
                  To book tickets, please contact our admin team using the contact information provided.
                </div>
              </div>
            </div>

            <CanvasSeatMapCanvas 
              seatMapData={seats} 
              selectedSeats={[]} 
              toggleSeat={dummyToggleSeat} 
            />
          </div>

          <div className="col-md-5 px-md-5">
            {/* Event Summary */}
            <div className="card mb-4 shadow-sm border-0">
              <div className="row g-3 p-3 align-items-center">
                <div className="col-2">
                  <img src={image} alt={title} className="img-fluid rounded" />
                </div>
                <div className="col-10">
                  <h5 className="mb-1 fw-semibold">{title}</h5>
                  <small className="text-muted">
                    {formatEventDateTimeFromAPI(eventData.date, eventData.time)} <span className="mx-2">|</span>
                    {venue?.name}
                  </small>
                </div>
              </div>
            </div>

            {/* Contact Admin Section */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                  Contact Our Team for Booking
                </h5>
              </div>
              <div className="card-body">
                <p className="text-muted mb-4">
                  To book tickets for this event, please contact our team using any of the methods below:
                </p>
                
                <div className="d-flex gap-3 align-items-center">
                  {/* Email Contact */}
                  <a 
                    href={`mailto:${contactInfo.email}?subject=Booking Request - ${title}&body=Hello,%0D%0A%0D%0AI would like to book tickets for ${title}.%0D%0A%0D%0AEvent Details:%0D%0A- Event: ${title}%0D%0A- Date: ${getFormattedDateTimeForLinks()}%0D%0A- Venue: ${venue?.name}%0D%0A%0D%0APlease provide information about:%0D%0A- Available seats%0D%0A- Pricing%0D%0A- Booking process%0D%0A%0D%0AThank you!`}
                    className="btn btn-primary d-flex align-items-center justify-content-between" style={{ width: 'fit-content' }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faEnvelope} className='me-2' />
                    </span>
                   {contactInfo.email}
                  </a>

                    <p className='mb-0'>  |  </p>
                  {/* WhatsApp Contact */}
                  <a 
                    href={`https://wa.me/${contactInfo.whatsapp}?text=Hello! I would like to book tickets for ${title}. Event: ${title} on ${getFormattedDateTimeForLinks()} at ${venue?.name}.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex align-items-center justify-content-between"
                    style={{ width: 'fit-content', color: 'black' }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faWhatsappBrand} size='xl' style={{ color: '#25D366' }}/>
                    </span>
                  </a>
                </div>
              </div>
            </div>

            {/* Venue Information */}
            <div className="card shadow-sm border-0">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                  Venue Information
                </h6>
              </div>
              <div className="card-body">
                <h6 className="fw-semibold">{venue?.name}</h6>
                <p className=" mb-3">{venue?.address}</p>
                
                <div className="d-flex align-items-center  mb-3">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                  {getFormattedDateTimeForLinks()}
                </div>
                
                <a
                  href={venue?.map_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary d-flex align-items-center"
                  style={{ width: 'fit-content' }}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                  View on Map
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ReadOnlyBookingPage; 